import { COIN_UPGRADE_BY_ID, upgradeCost, upgradeLevel, type CoinUpgradeId } from '../data/coinUpgrades';
import { TASK, TASK_IDS } from '../config/constants';
import { CHEESES } from '../data/cheeses';
import * as Cellar from '../core/Cellar';
import type { Game } from '../core/Game';

/** Goals and first-time feedback follow actual saved progress; they never pay currency. */
export class Rewards {
  private root = document.createElement('aside');
  private stamp: HTMLElement | null = null;
  private timer = 0;
  private disposeListeners: (() => void)[] = [];
  private baseline: { batches: number; sales: number; xp: number };
  private signature = '';
  constructor(private game: Game) {
    this.baseline = { batches:game.state.stats.batches,sales:game.state.stats.unitsSold,xp:game.state.xp.total };
    this.root.className='rewards-widget';this.root.setAttribute('aria-label','Farm goals');document.body.appendChild(this.root);
    // Existing saves open quietly; only new achievements this visit are celebrated.
    for(const [key] of this.achievements()) if(!game.state.rewardHistory.includes(key))game.state.rewardHistory.push(key);
    this.disposeListeners.push(game.on('change',()=>this.update()),game.on('day',()=>{
      const s=game.state;
      game.toast(`Day complete · ${s.stats.batches-this.baseline.batches} batches · ${s.stats.unitsSold-this.baseline.sales} units sold · ${(s.xp.total-this.baseline.xp).toFixed(1)} XP this visit. Your saved farm is ready for tomorrow.`, 'good');
      this.baseline={batches:s.stats.batches,sales:s.stats.unitsSold,xp:s.xp.total};
    }));
    this.update();
  }
  private achievements(): [string,string][] {
    const s=this.game.state, result:[string,string][]=[];
    if(s.learning.madeCheese)result.push(['first-cheese','Your first cheese — made by you!']);
    if(s.learning.agedCheese)result.push(['first-aged','Your first aged cheese!']);
    if(s.learning.soldCheese)result.push(['first-sale','Your first sale — the farm is growing!']);
    for(const id of ['shop','cellar','employees','field'])if(s.skills.includes(id))result.push([`milestone:${id}`,`${id.charAt(0).toUpperCase()+id.slice(1)} unlocked!`]);
    for(const id of s.codex.learned)if(id!=='mozzarella_fresh')result.push([`learned:${id}`,`Recipe learned: ${CHEESES[id]?.name??'Cheese'}`]);
    for(const id of TASK_IDS)if(this.game.isMastered(id))result.push([`mastery:${id}`,`${TASK[id].label} mastered — teach your crew!`]);
    return result;
  }
  update():void {
    const s=this.game.state;
    for(const [key,label]of this.achievements())if(!s.rewardHistory.includes(key)){
      s.rewardHistory.push(key);this.showStamp(label);this.game.save();
    }
    const goals:string[]=[];
    if(!s.bessie.fedToday)goals.push('Feed Bessie to start the day.');
    else if(!s.learning.madeCheese)goals.push('Make your first mozzarella.');
    else if(!s.learning.soldCheese)goals.push('Sell your finished cheese.');
    else if(s.cellar.racks.some(r=>Cellar.stackNeedsWash(r)))goals.push('Your cellar has rinds ready to wash.');
    else if(s.cellar.racks.some(r=>r.ageDays>=35))goals.push('Check aged cheese ready to wrap.');
    else goals.push('Craft a batch and improve your skills.');
    let pinned:HTMLElement|null=null;
    const id=s.pinnedUpgradeId as CoinUpgradeId|null;
    if(id&&COIN_UPGRADE_BY_ID[id]){
      const u=COIN_UPGRADE_BY_ID[id],level=upgradeLevel(s,id);
      if(level<15){const cost=upgradeCost(id,level+1);pinned=document.createElement('div');
        const label=document.createElement('small');label.textContent=`${u.name} ${level+1}/15 · ${s.gold.toFixed(2)} / ${cost} coins`;
        const progress=document.createElement('progress');progress.max=cost;progress.value=Math.min(cost,s.gold);progress.setAttribute('aria-label',`Coins toward ${u.name}`);pinned.append(label,progress);
      }
    }else if(!this.game.hasShop)goals.push('Save 100 coins to open the Shop.');
    else if(!this.game.hasSkill('cellar'))goals.push('Save 200 coins to open the Cellar.');
    else if(!s.learning.agedCheese)goals.push('Age and wrap your first Taleggio.');
    const nextTask=TASK_IDS.find(t=>t!=='feed'&&t!=='sell'&&!this.game.isMastered(t));
    if(nextTask)goals.push(`${TASK[nextTask].label}: level ${this.game.taskLevel(nextTask)} — progress toward mastery.`);
    const signature=JSON.stringify([goals.slice(0,pinned?2:3),id,s.gold.toFixed(2),id?upgradeLevel(s,id):0]);
    if(signature===this.signature)return;this.signature=signature;
    this.root.replaceChildren();const heading=document.createElement('b');heading.textContent='Your next chapter';this.root.appendChild(heading);
    goals.slice(0,pinned?2:3).forEach((label,i)=>{const row=document.createElement('small');row.textContent=`○ ${label}`;if(i>0)row.className='extra-goal';this.root.appendChild(row);});
    if(pinned)this.root.appendChild(pinned);
  }
  private showStamp(label:string):void {
    clearTimeout(this.timer);this.stamp?.remove();this.stamp=document.createElement('div');this.stamp.className='reward-stamp';this.stamp.setAttribute('role','status');this.stamp.textContent=`✓ ${label}`;document.body.appendChild(this.stamp);
    this.timer=window.setTimeout(()=>{this.stamp?.remove();this.stamp=null;},2200);
  }
  dispose():void {this.disposeListeners.forEach(dispose=>dispose());clearTimeout(this.timer);this.stamp?.remove();this.root.remove();}
}
